/**
 * Custom Shopping List
 * ---------------------------------------------------------
 * Reads and writes SparkLayer shopping list data via
 * SparkLayer's own GraphQL app-proxy endpoint (same one the
 * built-in widget uses), then renders a full-width table with
 * inline quantity editing and one-click add-to-cart.
 *
 * IMPORTANT — before going live, confirm these two values
 * match what's in the SparkLayer script tag already installed
 * on this theme (Online Store > Themes > Edit code > search
 * for "sparklayer"). They were correct as of the DevTools
 * capture used to build this, but SparkLayer can rev its
 * script version over time.
 */
(function () {
  'use strict';

  var CONFIG = {
    siteId: 'unisandirect5x',
    // SparkLayer doesn't expose its current script version anywhere
    // readable client-side, so this has to be hardcoded. Based on
    // testing, this value doesn't appear to be strictly validated by
    // SparkLayer's server (calls succeeded using an older version
    // number while the site's own script was on a newer one), so this
    // is unlikely to need updating. If shopping list calls ever start
    // failing for no clear reason, this is worth checking first,
    // search this theme's code for "sparklayer" to find the current
    // value in SparkLayer's own script tag.
    version: '4.6.11',
    endpoint: '/tools/sparklayer/graphql',
    // Set to a customer ID string if this page is ever used by a
    // sales rep viewing a customer's list on their behalf. Left
    // null for standard customer self-service use.
    impersonatingCustomerId: null,
  };

  var root = document.getElementById('csl-root');
  if (!root) return;

  var els = {
    status: document.getElementById('csl-status'),
    tableWrap: document.getElementById('csl-table-wrap'),
    rows: document.getElementById('csl-rows'),
    empty: document.getElementById('csl-empty'),
    footer: document.getElementById('csl-footer'),
    itemCount: document.getElementById('csl-item-count'),
    addAllBtn: document.getElementById('csl-add-all'),
    listPicker: document.getElementById('csl-list-picker'),
    listSelect: document.getElementById('csl-list-select'),
    newListBtn: document.getElementById('csl-new-list-btn'),
    newListForm: document.getElementById('csl-new-list-form'),
    newListName: document.getElementById('csl-new-list-name'),
    newListCancel: document.getElementById('csl-new-list-cancel'),
    searchForm: document.getElementById('csl-search-form'),
    searchInput: document.getElementById('csl-search-input'),
    searchResults: document.getElementById('csl-search-results'),
  };

  var state = {
    lists: [],
    activeListId: null,
    activeList: null, // { id, name, contents: [{sku, quantity}] }
    variantsBySku: {}, // sku -> hydrated variant data
    saving: false,
  };

  var saveTimers = {};

  // ---------------------------------------------------------------
  // GraphQL helper
  // ---------------------------------------------------------------
  function graphql(query, variables) {
    var url =
      CONFIG.endpoint +
      '?siteId=' +
      encodeURIComponent(CONFIG.siteId) +
      '&v=' +
      encodeURIComponent(CONFIG.version);

    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Site-Id': CONFIG.siteId,
      },
      body: JSON.stringify({ query: query, variables: variables }),
    })
      .then(function (res) {
        if (!res.ok) {
          return res
            .json()
            .catch(function () {
              return null;
            })
            .then(function (body) {
              var detail = body && (body.detail || body.title);
              throw new Error(
                'Request failed (' + res.status + ')' + (detail ? ': ' + detail : '')
              );
            });
        }
        return res.json();
      })
      .then(function (json) {
        if (json.errors && json.errors.length) {
          throw new Error(json.errors[0].message || 'GraphQL error');
        }
        return json.data;
      });
  }

  var QUERIES = {
    shoppingLists:
      '#graphql\nquery shoppingLists($impersonatingCustomerId: String) {\n' +
      '  shoppingLists(impersonatingCustomerId: $impersonatingCustomerId) {\n' +
      '    id\n    name\n    updatedAt\n    lineItemCount\n' +
      '  }\n}',

    shoppingList:
      '#graphql\nquery shoppingList($id: String!, $impersonatingCustomerId: String) {\n' +
      '  shoppingList(id: $id, impersonatingCustomerId: $impersonatingCustomerId) {\n' +
      '    id\n    name\n    createdAt\n    updatedAt\n    lineItemCount\n' +
      '    contents {\n      sku\n      quantity\n    }\n' +
      '  }\n}',

    shoppingListUpdate:
      '#graphql\nmutation shoppingListUpdate($id: String!, $name: String, $contents: [ShoppingListItemInput!], $impersonatingCustomerId: String) {\n' +
      '  shoppingListUpdate(id: $id, name: $name, contents: $contents, impersonatingCustomerId: $impersonatingCustomerId) {\n' +
      '    id\n    name\n    updatedAt\n    lineItemCount\n' +
      '    contents {\n      sku\n      quantity\n    }\n' +
      '  }\n}',

    shoppingListCreate:
      '#graphql\nmutation shoppingListCreate($name: String!, $contents: [ShoppingListItemInput!]!, $impersonatingCustomerId: String) {\n' +
      '  shoppingListCreate(name: $name, contents: $contents, impersonatingCustomerId: $impersonatingCustomerId) {\n' +
      '    id\n    name\n    updatedAt\n    lineItemCount\n' +
      '  }\n}',

    fetchVariantsData:
      '#graphql\nquery fetchVariantsData($variants: [VariantInput!]!) {\n' +
      '  variants(variants: $variants) {\n' +
      '    id\n    externalId\n    parentExternalId\n    sku\n    name\n    slug\n' +
      '    cartImageUrl\n' +
      '    price {\n      net\n      gross\n      currencyCode\n    }\n' +
      '    priceBreaks {\n      quantity\n      price {\n        net\n        gross\n      }\n    }\n' +
      '    stockDisplay {\n      status\n      available\n    }\n' +
      '  }\n}',

    // Reconstructed from a partial capture; searchTerm is confirmed,
    // other variables/fields may need adjusting once tested live.
    productSearch:
      '#graphql\nquery productSearchV2($searchTerm: String!) {\n' +
      '  productSearch(searchTerm: $searchTerm) {\n' +
      '    product {\n' +
      '      id\n      externalId\n' +
      '      variants {\n' +
      '        id\n        externalId\n        parentExternalId\n        sku\n        name\n        slug\n' +
      '        cartImageUrl\n' +
      '        price {\n          net\n          gross\n          currencyCode\n        }\n' +
      '        stockDisplay {\n          status\n          available\n        }\n' +
      '      }\n' +
      '    }\n' +
      '  }\n}',
  };

  // ---------------------------------------------------------------
  // Formatting helpers
  // ---------------------------------------------------------------
  function formatMoney(amount, currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'USD',
      }).format(amount);
    } catch (e) {
      return '$' + Number(amount).toFixed(2);
    }
  }

  // Picks the correct bulk price tier for a given quantity.
  function priceForQuantity(variant, quantity) {
    var best = variant.price;
    if (variant.priceBreaks && variant.priceBreaks.length) {
      variant.priceBreaks.forEach(function (tier) {
        if (quantity >= tier.quantity) {
          best = tier.price;
        }
      });
    }
    return best;
  }

  function setStatus(message, isError) {
    els.status.textContent = message || '';
    els.status.classList.toggle('csl__status--error', !!isError);
  }

  // ---------------------------------------------------------------
  // Load flow
  // ---------------------------------------------------------------
  // spark-redirect.js loads for every visitor regardless of login
  // state, so its presence alone tells us nothing (confirmed via
  // testing). SparkLayer's real SDK script (served from
  // sparkcdn.io/sparkjs/...) only loads for authenticated sessions,
  // so we check for that instead. If it's already in the page's HTML,
  // we know immediately we're logged in and can skip waiting. If it's
  // not there yet, that's inconclusive (it may still be loading), so
  // we fall back to polling for window.spark before deciding.
  function isRealSparkScriptPresent() {
    var scripts = document.querySelectorAll('script[src]');
    for (var i = 0; i < scripts.length; i++) {
      if (scripts[i].src.indexOf('sparkcdn.io/sparkjs') !== -1) {
        return true;
      }
    }
    return false;
  }

  function waitForSpark(timeoutMs) {
    var start = Date.now();
    return new Promise(function (resolve) {
      (function check() {
        if (window.spark && typeof window.spark.isLoggedIn === 'function') {
          resolve(window.spark);
        } else if (Date.now() - start > timeoutMs) {
          resolve(null);
        } else {
          setTimeout(check, 100);
        }
      })();
    });
  }

  function redirectToLogin() {
    setStatus('Redirecting you to login…');
    var returnUrl = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    window.location.href = '/account/login?return_url=' + returnUrl;
  }

  function init() {
    // Never redirect while inside Shopify's theme editor (Customize
    // screen) - that admin session isn't a real customer login, and
    // redirecting there breaks the ability to edit this page at all.
    var inThemeEditor = !!(window.Shopify && window.Shopify.designMode);

    setStatus('Checking your account…');

    if (isRealSparkScriptPresent()) {
      // Known logged-in signal already present; no need to wait.
      setStatus('Loading your shopping list…');
      loadShoppingLists();
      return;
    }

    waitForSpark(1500).then(function (spark) {
      if (!spark) {
        if (inThemeEditor) {
          setStatus(
            'Preview note: log in as a customer on the live storefront to see real shopping list data here.'
          );
          return;
        }
        redirectToLogin();
        return;
      }

      spark
        .isLoggedIn()
        .then(function (loggedIn) {
          if (!loggedIn) {
            if (inThemeEditor) {
              setStatus(
                'Preview note: log in as a customer on the live storefront to see real shopping list data here.'
              );
              return;
            }
            redirectToLogin();
            return;
          }
          setStatus('Loading your shopping list…');
          return loadShoppingLists();
        })
        .catch(function () {
          // If the login check itself fails after spark did load,
          // fall back to attempting the normal load.
          return loadShoppingLists();
        });
    });
  }

  function loadShoppingLists(preferredListId) {
    return graphql(QUERIES.shoppingLists, {
      impersonatingCustomerId: CONFIG.impersonatingCustomerId,
    })
      .then(function (data) {
        state.lists = (data && data.shoppingLists) || [];

        if (!state.lists.length) {
          showEmpty();
          return;
        }

        if (state.lists.length > 1) {
          populateListPicker(state.lists);
        }

        var targetId =
          preferredListId &&
          state.lists.some(function (l) {
            return l.id === preferredListId;
          })
            ? preferredListId
            : state.lists[0].id;

        if (els.listSelect) {
          els.listSelect.value = targetId;
        }

        return loadList(targetId);
      })
      .catch(function (err) {
        setStatus(
          'We couldn\'t load your shopping list right now. Please refresh the page or try again shortly.',
          true
        );
        // eslint-disable-next-line no-console
        console.error('[custom-shopping-list]', err);
      });
  }

  function populateListPicker(lists) {
    els.listPicker.hidden = false;
    els.listSelect.innerHTML = '';
    lists.forEach(function (list) {
      var opt = document.createElement('option');
      opt.value = list.id;
      opt.textContent = list.name + ' (' + list.lineItemCount + ')';
      els.listSelect.appendChild(opt);
    });
    els.listSelect.addEventListener('change', function () {
      loadList(els.listSelect.value);
    });
  }

  // ---------------------------------------------------------------
  // Create a new list
  // ---------------------------------------------------------------
  if (els.newListBtn) {
    els.newListBtn.addEventListener('click', function () {
      els.newListForm.hidden = false;
      els.newListBtn.hidden = true;
      els.newListName.value = '';
      els.newListName.focus();
    });
  }

  if (els.newListCancel) {
    els.newListCancel.addEventListener('click', function () {
      els.newListForm.hidden = true;
      els.newListBtn.hidden = false;
    });
  }

  if (els.newListForm) {
    els.newListForm.addEventListener('submit', function (evt) {
      evt.preventDefault();
      var name = els.newListName.value.trim();
      if (!name) return;
      createNewList(name);
    });
  }

  // ---------------------------------------------------------------
  // Product search: add items to the active list from this page
  // ---------------------------------------------------------------
  if (els.searchForm) {
    els.searchForm.addEventListener('submit', function (evt) {
      evt.preventDefault();
      var term = els.searchInput.value.trim();
      if (!term) return;
      runProductSearch(term);
    });
  }

  function runProductSearch(term) {
    els.searchResults.hidden = false;
    els.searchResults.innerHTML = '';
    var loadingMsg = document.createElement('p');
    loadingMsg.className = 'csl__search-status';
    loadingMsg.textContent = 'Searching…';
    els.searchResults.appendChild(loadingMsg);

    graphql(QUERIES.productSearch, { searchTerm: term })
      .then(function (data) {
        var results = (data && data.productSearch) || [];
        renderSearchResults(results);
      })
      .catch(function (err) {
        els.searchResults.innerHTML = '';
        var errMsg = document.createElement('p');
        errMsg.className = 'csl__search-status csl__search-status--error';
        errMsg.textContent = 'Search failed. Please try again.';
        els.searchResults.appendChild(errMsg);
        // eslint-disable-next-line no-console
        console.error('[custom-shopping-list]', err);
      });
  }

  function renderSearchResults(results) {
    els.searchResults.innerHTML = '';

    if (!results.length) {
      var noneMsg = document.createElement('p');
      noneMsg.className = 'csl__search-status';
      noneMsg.textContent = 'No products found.';
      els.searchResults.appendChild(noneMsg);
      return;
    }

    results.forEach(function (result) {
      var variant =
        result.product && result.product.variants && result.product.variants[0];
      if (!variant) return;

      var row = document.createElement('div');
      row.className = 'csl__search-result';

      var img = document.createElement('img');
      img.className = 'csl__thumb';
      img.loading = 'lazy';
      img.alt = variant.name;
      img.src = variant.cartImageUrl || '';
      row.appendChild(img);

      var info = document.createElement('div');
      info.className = 'csl__search-result-info';
      var name = document.createElement('span');
      name.textContent = variant.name;
      info.appendChild(name);
      var price = document.createElement('span');
      price.className = 'csl__item-sku';
      price.textContent = formatMoney(
        variant.price.net,
        variant.price.currencyCode
      );
      info.appendChild(price);
      row.appendChild(info);

      var addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'csl__button csl__button--small';
      addBtn.textContent = 'Add to list';
      addBtn.addEventListener('click', function () {
        addProductToActiveList(variant.sku, addBtn);
      });
      row.appendChild(addBtn);

      els.searchResults.appendChild(row);
    });
  }

  function addProductToActiveList(sku, btnEl) {
    if (!state.activeList) {
      setStatus(
        'Please create or select a list before adding products.',
        true
      );
      return;
    }

    var originalText = btnEl.textContent;
    btnEl.disabled = true;
    btnEl.textContent = 'Adding…';

    var contents = currentContents();
    var existing = contents.filter(function (c) {
      return c.sku === sku;
    })[0];

    if (existing) {
      existing.quantity += 1;
    } else {
      contents.push({ sku: sku, quantity: 1 });
    }

    persistList(contents)
      .then(function () {
        setStatus('Added to your list.');
        return loadList(state.activeList.id);
      })
      .catch(function (err) {
        setStatus('Could not add that product. Please try again.', true);
        // eslint-disable-next-line no-console
        console.error('[custom-shopping-list]', err);
      })
      .finally(function () {
        btnEl.disabled = false;
        btnEl.textContent = originalText;
      });
  }

  function createNewList(name) {
    var submitBtn = els.newListForm.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Creating…';
    setStatus('Creating your new list…');

    graphql(QUERIES.shoppingListCreate, {
      name: name,
      contents: [],
      impersonatingCustomerId: CONFIG.impersonatingCustomerId,
    })
      .then(function (data) {
        var newList = data.shoppingListCreate;
        els.newListForm.hidden = true;
        els.newListBtn.hidden = false;

        // Refresh the full list of lists so the picker/dropdown stay
        // accurate, and land directly on the newly created list.
        return loadShoppingLists(newList && newList.id);
      })
      .catch(function (err) {
        setStatus('Could not create that list. Please try again.', true);
        // eslint-disable-next-line no-console
        console.error('[custom-shopping-list]', err);
      })
      .finally(function () {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Create';
      });
  }

  function loadList(listId) {
    state.activeListId = listId;
    setStatus('Loading your shopping list…');
    els.tableWrap.hidden = true;
    els.footer.hidden = true;
    els.empty.hidden = true;

    return graphql(QUERIES.shoppingList, {
      id: listId,
      impersonatingCustomerId: CONFIG.impersonatingCustomerId,
    })
      .then(function (data) {
        state.activeList = data.shoppingList;

        if (!state.activeList.contents.length) {
          showEmpty();
          return;
        }

        var skus = state.activeList.contents.map(function (item) {
          return { sku: item.sku };
        });

        return graphql(QUERIES.fetchVariantsData, {
          variants: skus,
          impersonatingCustomerId: CONFIG.impersonatingCustomerId,
        }).then(function (variantData) {
          state.variantsBySku = {};
          (variantData.variants || []).forEach(function (v) {
            state.variantsBySku[v.sku] = v;
          });
          renderRows();
        });
      })
      .catch(function (err) {
        setStatus(
          'We couldn\'t load that list right now. Please refresh the page or try again shortly.',
          true
        );
        // eslint-disable-next-line no-console
        console.error('[custom-shopping-list]', err);
      });
  }

  function showEmpty() {
    setStatus('');
    els.tableWrap.hidden = true;
    els.footer.hidden = true;
    els.empty.hidden = false;
  }

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  function renderRows() {
    setStatus('');
    els.rows.innerHTML = '';

    state.activeList.contents.forEach(function (item) {
      var variant = state.variantsBySku[item.sku];
      if (!variant) {
        els.rows.appendChild(buildUnavailableRow(item));
        return;
      }
      els.rows.appendChild(buildRow(item, variant));
    });

    els.tableWrap.hidden = false;
    els.footer.hidden = false;
    updateItemCount();
  }

  // Shown when SparkLayer can't resolve a saved SKU to a real product
  // (e.g. discontinued or removed from the catalog), instead of
  // silently dropping the item with no explanation.
  function buildUnavailableRow(item) {
    var tr = document.createElement('tr');
    tr.className = 'csl__row csl__row--unavailable';
    tr.dataset.sku = item.sku;

    var tdImg = document.createElement('td');
    tdImg.className = 'csl__cell csl__cell--image';

    var tdName = document.createElement('td');
    tdName.className = 'csl__cell csl__cell--name';
    tdName.setAttribute('colspan', '3');
    var nameWrap = document.createElement('div');
    nameWrap.className = 'csl__item-name';
    var msg = document.createElement('span');
    msg.textContent = 'This item is no longer available.';
    nameWrap.appendChild(msg);
    var skuEl = document.createElement('span');
    skuEl.className = 'csl__item-sku';
    skuEl.textContent = 'SKU: ' + item.sku;
    nameWrap.appendChild(skuEl);
    tdName.appendChild(nameWrap);

    var tdAction = document.createElement('td');
    tdAction.className = 'csl__cell csl__cell--action';
    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'csl__remove';
    removeBtn.textContent = 'Remove';
    removeBtn.addEventListener('click', function () {
      removeItem(item.sku, tr);
    });
    tdAction.appendChild(removeBtn);

    tr.appendChild(tdImg);
    tr.appendChild(tdName);
    tr.appendChild(tdAction);

    return tr;
  }

  function buildRow(item, variant) {
    var tr = document.createElement('tr');
    tr.className = 'csl__row';
    tr.dataset.sku = item.sku;

    var unitPrice = priceForQuantity(variant, item.quantity);
    var inStock = !variant.stockDisplay || variant.stockDisplay.available !== false;

    // Image
    var tdImg = document.createElement('td');
    tdImg.className = 'csl__cell csl__cell--image';
    var img = document.createElement('img');
    img.className = 'csl__thumb';
    img.loading = 'lazy';
    img.alt = variant.name;
    img.src = variant.cartImageUrl || '';
    tdImg.appendChild(img);

    // Name
    var tdName = document.createElement('td');
    tdName.className = 'csl__cell csl__cell--name';
    var nameWrap = document.createElement('div');
    nameWrap.className = 'csl__item-name';
    var link = document.createElement('a');
    link.href = '/products/' + variant.slug;
    link.textContent = variant.name;
    nameWrap.appendChild(link);
    var skuEl = document.createElement('span');
    skuEl.className = 'csl__item-sku';
    skuEl.textContent = 'SKU: ' + variant.sku;
    nameWrap.appendChild(skuEl);
    if (!inStock) {
      var stockBadge = document.createElement('span');
      stockBadge.className = 'csl__stock-badge';
      stockBadge.textContent = 'Out of stock';
      nameWrap.appendChild(stockBadge);
    }
    tdName.appendChild(nameWrap);

    // Price
    var tdPrice = document.createElement('td');
    tdPrice.className = 'csl__cell csl__cell--price';
    tdPrice.textContent = formatMoney(unitPrice.net, unitPrice.currencyCode);

    // Quantity
    var tdQty = document.createElement('td');
    tdQty.className = 'csl__cell csl__cell--qty';
    tdQty.appendChild(buildQtyControl(item, variant));

    // Total
    var tdTotal = document.createElement('td');
    tdTotal.className = 'csl__cell csl__cell--total';
    tdTotal.textContent = formatMoney(
      unitPrice.net * item.quantity,
      unitPrice.currencyCode
    );

    // Action (add to cart + remove)
    var tdAction = document.createElement('td');
    tdAction.className = 'csl__cell csl__cell--action';

    var addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'csl__button csl__button--small';
    addBtn.textContent = 'Add to cart';
    addBtn.addEventListener('click', function () {
      addSingleItemToCart(item.sku, item.quantity, addBtn);
    });
    tdAction.appendChild(addBtn);

    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'csl__remove';
    removeBtn.textContent = 'Remove';
    removeBtn.addEventListener('click', function () {
      removeItem(item.sku, tr);
    });
    tdAction.appendChild(removeBtn);

    tr.appendChild(tdImg);
    tr.appendChild(tdName);
    tr.appendChild(tdPrice);
    tr.appendChild(tdQty);
    tr.appendChild(tdTotal);
    tr.appendChild(tdAction);

    return tr;
  }

  function buildQtyControl(item, variant) {
    var wrap = document.createElement('div');
    wrap.className = 'csl__qty';

    var minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'csl__qty-btn';
    minus.textContent = '\u2212';
    minus.setAttribute('aria-label', 'Decrease quantity');

    var input = document.createElement('input');
    input.type = 'number';
    input.className = 'csl__qty-input';
    input.min = '1';
    input.value = item.quantity;
    input.setAttribute('aria-label', 'Quantity for ' + variant.name);

    var plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'csl__qty-btn';
    plus.textContent = '+';
    plus.setAttribute('aria-label', 'Increase quantity');

    function applyChange(newQty) {
      newQty = Math.max(1, parseInt(newQty, 10) || 1);
      input.value = newQty;
      handleQuantityChange(item.sku, newQty, input.closest('tr'));
    }

    minus.addEventListener('click', function () {
      applyChange(parseInt(input.value, 10) - 1);
    });
    plus.addEventListener('click', function () {
      applyChange(parseInt(input.value, 10) + 1);
    });
    input.addEventListener('change', function () {
      applyChange(input.value);
    });

    wrap.appendChild(minus);
    wrap.appendChild(input);
    wrap.appendChild(plus);
    return wrap;
  }

  // ---------------------------------------------------------------
  // Mutations (save / remove)
  // ---------------------------------------------------------------
  function currentContents() {
    return state.activeList.contents.map(function (item) {
      return { sku: item.sku, quantity: item.quantity };
    });
  }

  function persistList(contents) {
    return graphql(QUERIES.shoppingListUpdate, {
      id: state.activeList.id,
      name: state.activeList.name,
      contents: contents,
      impersonatingCustomerId: CONFIG.impersonatingCustomerId,
    }).then(function (data) {
      state.activeList = data.shoppingListUpdate;
    });
  }

  function handleQuantityChange(sku, newQty, rowEl) {
    var item = state.activeList.contents.filter(function (c) {
      return c.sku === sku;
    })[0];
    if (!item) return;

    var previousQty = item.quantity;
    item.quantity = newQty;

    var variant = state.variantsBySku[sku];

    function updateRowDisplay(qty) {
      if (!variant || !rowEl) return;
      var unitPrice = priceForQuantity(variant, qty);
      var totalCell = rowEl.querySelector('.csl__cell--total');
      var priceCell = rowEl.querySelector('.csl__cell--price');
      var qtyInput = rowEl.querySelector('.csl__qty-input');
      if (totalCell) {
        totalCell.textContent = formatMoney(
          unitPrice.net * qty,
          unitPrice.currencyCode
        );
      }
      if (priceCell) {
        priceCell.textContent = formatMoney(unitPrice.net, unitPrice.currencyCode);
      }
      if (qtyInput) {
        qtyInput.value = qty;
      }
    }

    // Update the total cell immediately for responsiveness.
    updateRowDisplay(newQty);

    // Debounce saves so rapid clicks on +/- don't fire a request each time.
    clearTimeout(saveTimers[sku]);
    saveTimers[sku] = setTimeout(function () {
      persistList(currentContents()).catch(function (err) {
        // Save failed: roll back so the page doesn't silently drift
        // from what's actually saved on the server.
        item.quantity = previousQty;
        updateRowDisplay(previousQty);
        setStatus('Could not save that change. Please try again.', true);
        // eslint-disable-next-line no-console
        console.error('[custom-shopping-list]', err);
      });
    }, 500);
  }

  function removeItem(sku, rowEl) {
    if (rowEl) rowEl.classList.add('is-removing');

    var remaining = state.activeList.contents.filter(function (c) {
      return c.sku !== sku;
    });

    persistList(remaining)
      .then(function () {
        delete state.variantsBySku[sku];
        if (!state.activeList.contents.length) {
          showEmpty();
        } else if (rowEl) {
          rowEl.remove();
          updateItemCount();
        }
      })
      .catch(function (err) {
        if (rowEl) rowEl.classList.remove('is-removing');
        setStatus('Could not remove that item. Please try again.', true);
        // eslint-disable-next-line no-console
        console.error('[custom-shopping-list]', err);
      });
  }

  function updateItemCount() {
    var count = state.activeList.contents.length;
    els.itemCount.textContent =
      count + (count === 1 ? ' item' : ' items') + ' in this list';
  }

  // ---------------------------------------------------------------
  // Add all to cart
  // ---------------------------------------------------------------
  function addAllToCart() {
    if (state.saving) return;

    if (!window.spark || typeof window.spark.updateCart !== 'function') {
      setStatus(
        'Cart integration is not available on this page right now. Please refresh and try again.',
        true
      );
      return;
    }

    state.saving = true;
    els.addAllBtn.disabled = true;
    els.addAllBtn.textContent = 'Adding…';

    var products = state.activeList.contents.map(function (item) {
      return { sku: item.sku, adjustQuantity: item.quantity };
    });

    window.spark
      .updateCart({ products: products })
      .then(function () {
        setStatus('Added to cart.');
        if (typeof window.spark.openDrawer === 'function') {
          window.spark.openDrawer();
        }
      })
      .catch(function (err) {
        setStatus(
          'Something went wrong adding these items to your cart. Please try again.',
          true
        );
        // eslint-disable-next-line no-console
        console.error('[custom-shopping-list]', err);
      })
      .finally(function () {
        state.saving = false;
        els.addAllBtn.disabled = false;
        els.addAllBtn.textContent = 'Add all to cart';
      });
  }

  els.addAllBtn.addEventListener('click', addAllToCart);

  // ---------------------------------------------------------------
  // Add a single item to cart (per-row button)
  // ---------------------------------------------------------------
  function addSingleItemToCart(sku, quantity, btnEl) {
    if (!window.spark || typeof window.spark.updateCart !== 'function') {
      setStatus(
        'Cart integration is not available on this page right now. Please refresh and try again.',
        true
      );
      return;
    }

    var originalText = btnEl.textContent;
    btnEl.disabled = true;
    btnEl.textContent = 'Adding…';

    window.spark
      .updateCart({ products: [{ sku: sku, adjustQuantity: quantity }] })
      .then(function () {
        setStatus('Added to cart.');
        if (typeof window.spark.openDrawer === 'function') {
          window.spark.openDrawer();
        }
      })
      .catch(function (err) {
        setStatus(
          'Something went wrong adding that item to your cart. Please try again.',
          true
        );
        // eslint-disable-next-line no-console
        console.error('[custom-shopping-list]', err);
      })
      .finally(function () {
        btnEl.disabled = false;
        btnEl.textContent = originalText;
      });
  }

  init();
})();
